/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: GameContext.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  26.09.22 12:44 pm.
 * Last modified: 26.09.22 12:44 pm.
 *
 * More information at: https://egt-digital.com/
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';
import store from '../eventStore';
import ISettings, { defaultSettings } from '../interfaces/ISettings';

interface IContextType {
    settings: ISettings;
    setSettings?: React.Dispatch<React.SetStateAction<ISettings>>;
    subject?: BehaviorSubject<ISettings>;
}

const SettingsContext = createContext<IContextType>({
    settings: defaultSettings,
});

export const SettingsProvider = ({
    value: subject,
    children,
}: {
    value: BehaviorSubject<ISettings>;
    children: any;
}) => {
    const [settings, setSettings] = useState<ISettings>(subject.value || defaultSettings);

    useEffect(() => {
        const subscription = subject.subscribe(value => {
            if (value) {
                setSettings(value);
            }
        });

        return () => subscription.unsubscribe();
    }, [subject]);

    return <SettingsContext.Provider value={{ settings, setSettings, subject }}>{children}</SettingsContext.Provider>;
};

export const useSettings = (): [
    ISettings,
    React.Dispatch<React.SetStateAction<ISettings>> | ((settings: ISettings) => void) | undefined,
] => {
    const { settings, setSettings: setSettingsReactDispatcher, subject } = useContext(SettingsContext);
    const setSettings = subject ? (newSettings: ISettings) => subject.next(newSettings) : setSettingsReactDispatcher;

    return [settings, setSettings];
};

export const useSelectedSection = (): [string, (selectedSection: string) => void] => {
    const [settings, setSettings] = useSettings();

    const setSelectedSection = setSettings
        ? (sectionName: string) => {
              setSettings({
                  ...settings,
                  selectedSection: sectionName,
              });
              store.SELECT_CATEGORY.next(sectionName);
          }
        : (sectionName: string) => {
              store.SELECT_CATEGORY.next(sectionName);
          };

    return [settings.selectedSection, setSelectedSection];
};

export const useLobbyHost = () => {
    const { settings } = useContext(SettingsContext);

    return settings.host;
};
